// ABOUTME: Email change verification page that confirms new email address
// ABOUTME: Handles token-based email verification after user requests email change

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Container, Typography, Paper, Box, CircularProgress, Alert } from '@mui/material';
import axios from 'axios';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export default function VerifyEmailChange() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [verifying, setVerifying] = useState(false);
  const [verified, setVerified] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const token = searchParams.get('token');

  // Auto-trigger verification on mount when token exists
  useEffect(() => {
    if (token) {
      handleVerify();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handleVerify intentionally omitted; it is stable and adding it would cause extra invocations
  }, [token]);

  const handleVerify = async () => {
    if (!token) return;

    setVerifying(true);
    setError(null);
    try {
      const response = await api.post('/api/user/verify-email-change', { token });

      if (response.status === 200) {
        setVerified(true);

        // Log out the user to clear auth context and localStorage
        await logout();

        // Redirect to login after 3 seconds
        setTimeout(() => {
          navigate('/login?message=Email changed successfully. Please log in with your new email address.');
        }, 3000);
      }
    } catch (err: unknown) {
      const responseMessage = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message
        : undefined;
      setError(responseMessage || 'Invalid or expired verification token');
    } finally {
      setVerifying(false);
    }
  };

  if (!token) {
    return (
      <Container maxWidth="sm">
        <Paper sx={{ p: 4, mt: 4 }}>
          <Typography variant="h5" gutterBottom>Invalid Link</Typography>
          <Typography>This verification link is invalid.</Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/login')}
            sx={{ mt: 2 }}
          >
            Go to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm">
      <Paper sx={{ p: 4, mt: 4 }}>
        <Typography variant="h5" gutterBottom>Confirm Email Change</Typography>

        {verified ? (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              Your email address has been successfully changed!
            </Alert>
            <Typography sx={{ mb: 2 }}>
              You will be redirected to login with your new email address...
            </Typography>
            <Box display="flex" justifyContent="center">
              <CircularProgress size={24} />
            </Box>
          </Box>
        ) : error ? (
          <>
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </>
        ) : verifying ? (
          <Box>
            <Typography sx={{ mb: 3, textAlign: 'center' }}>
              Verifying your email change...
            </Typography>
            <Box display="flex" justifyContent="center">
              <CircularProgress size={40} />
            </Box>
          </Box>
        ) : null}
      </Paper>
    </Container>
  );
}
